import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { ChevronRight } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  bsValue?: string;
  icon?: ReactNode;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  badge?: ReactNode;
  className?: string;
  onClick?: () => void;
}

export function StatCard({
  title,
  value,
  subtitle,
  bsValue,
  icon,
  trend,
  badge,
  className,
  onClick,
}: StatCardProps) {
  const isClickable = Boolean(onClick);

  return (
    <div
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={clsx(
        'bg-surface border border-border rounded-xl p-4 shadow-xs space-y-2 select-none relative transition-all duration-200',
        isClickable &&
          'cursor-pointer hover:border-brand-primary/40 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 group focus:outline-none focus:ring-2 focus:ring-brand-primary/30',
        className
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="text-xs font-semibold text-text-secondary truncate">{title}</span>
          {badge}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {icon && (
            <div className="text-brand-primary p-2 bg-brand-primary/10 rounded-lg group-hover:bg-brand-primary/20 transition-colors">
              {icon}
            </div>
          )}
          {isClickable && (
            <ChevronRight
              size={14}
              className="text-text-muted opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all hidden sm:block"
            />
          )}
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl font-bold text-text-primary tracking-tight">{value}</span>
          {trend && (
            <span
              className={clsx(
                'text-xs font-medium px-1.5 py-0.5 rounded-full',
                trend.isPositive
                  ? 'text-brand-success bg-brand-success/10'
                  : 'text-brand-danger bg-brand-danger/10'
              )}
            >
              {trend.isPositive ? '↑' : '↓'} {trend.value}
            </span>
          )}
        </div>

        {bsValue && (
          <div className="inline-flex items-center pt-0.5">
            <span className="text-[11px] font-semibold px-2 py-0.5 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 rounded-md">
              ≈ {bsValue}
            </span>
          </div>
        )}
      </div>

      {subtitle && <p className="text-[11px] text-text-muted">{subtitle}</p>}
    </div>
  );
}
